import React from 'react';
import './Home.css';
import '../styles/eneos.css';
import { Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

const Home = () => {
  const organizations = [
    {
      type: 'retail',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80',
      title: 'Retail Businesses',
      description: 'Modern retail requires seamless integration between online and offline channels, real-time inventory tracking, and personalized customer experiences to stay competitive.',
      problem: 'Manual inventory tracking leads to stockouts and overstocking. Disconnected sales channels create customer frustration. Limited customer insights prevent personalized marketing.',
      solution: 'Integrated POS and e-commerce platform with real-time inventory sync, customer loyalty program, and automated marketing campaigns based on purchase history.',
      impact: [
        '35% reduction in inventory costs',
        '50% increase in customer retention',
        '3x faster checkout process',
        '40% boost in online sales'
      ]
    },
    {
      type: 'education',
      image: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1200&q=80',
      title: 'Educational Institutions',
      description: 'Schools need efficient systems to manage student records, facilitate online learning, track attendance, and maintain communication between teachers, students, and parents.',
      problem: 'Paper-based records are time-consuming and error-prone. Limited parent-teacher communication. Difficulty tracking student progress across multiple subjects and years.',
      solution: 'Comprehensive student information system with online learning portal, automated attendance tracking, grade management, and real-time parent notifications.',
      impact: [
        '70% reduction in administrative time',
        '90% parent engagement rate',
        'Real-time progress tracking',
        '60% faster report generation'
      ]
    },
    {
      type: 'healthcare',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1200&q=80',
      title: 'Healthcare Facilities',
      description: 'Healthcare providers need secure patient record management, efficient appointment scheduling, and seamless coordination between departments to deliver quality care.',
      problem: 'Fragmented patient records across departments. Long wait times due to manual scheduling. Medication errors from illegible prescriptions. Limited access to patient history.',
      solution: 'Electronic health records system with integrated appointment scheduling, e-prescriptions, lab results tracking, and secure patient portal for accessing medical history.',
      impact: [
        '80% reduction in medical errors',
        '45% decrease in wait times',
        'Instant access to patient history',
        '99.9% data security compliance'
      ]
    },
    {
      type: 'manufacturing',
      image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=1200&q=80',
      title: 'Manufacturing Plants',
      description: 'Manufacturers need to optimize production processes, maintain quality standards, predict equipment failures, and manage complex supply chains efficiently.',
      problem: 'Unexpected equipment downtime costs thousands per hour. Quality issues discovered too late. Inefficient production scheduling. Poor visibility into supply chain.',
      solution: 'Manufacturing execution system with IoT sensors for predictive maintenance, automated quality control, production optimization algorithms, and supply chain visibility.',
      impact: [
        '60% reduction in downtime',
        '95% quality compliance rate',
        '30% increase in productivity',
        '25% lower operational costs'
      ]
    },
    {
      type: 'finance',
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1200&q=80',
      title: 'Financial Services',
      description: 'Financial institutions require secure transaction processing, regulatory compliance, fraud detection, and real-time reporting to maintain trust and operational efficiency.',
      problem: 'Legacy systems slow down transactions. Manual compliance checks are error-prone. Fraud detection is reactive. Limited real-time insights into financial operations.',
      solution: 'Secure banking platform with automated compliance monitoring, AI-powered fraud detection, real-time transaction processing, and comprehensive reporting dashboards.',
      impact: [
        '99.99% transaction uptime',
        '85% faster fraud detection',
        'Full regulatory compliance',
        '50% reduction in processing time'
      ]
    },
    {
      type: 'real-estate',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80',
      title: 'Real Estate',
      description: 'Real estate businesses need property management systems, virtual tours, client relationship management, and transaction tracking to streamline operations.',
      problem: 'Manual property listings are time-consuming. Limited property visibility. Difficult to track client interactions. Complex transaction management.',
      solution: 'Integrated property management platform with virtual tour capabilities, CRM system, automated listing syndication, and transaction workflow management.',
      impact: [
        '60% faster property closings',
        '45% more qualified leads',
        '70% time saved on admin',
        '95% client satisfaction'
      ]
    },
    {
      type: 'logistics',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80',
      title: 'Logistics & Transportation',
      description: 'Logistics companies need route optimization, fleet management, real-time tracking, and warehouse management to ensure efficient operations.',
      problem: 'Inefficient routing increases fuel costs. Poor fleet visibility. Manual warehouse operations. Limited shipment tracking capabilities.',
      solution: 'Comprehensive logistics platform with AI-powered route optimization, GPS fleet tracking, automated warehouse management, and customer shipment portals.',
      impact: [
        '40% fuel cost savings',
        '55% faster deliveries',
        '99% on-time delivery rate',
        '35% operational cost reduction'
      ]
    },
    {
      type: 'media',
      image: 'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=1200&q=80',
      title: 'Media & Entertainment',
      description: 'Media companies need content management, streaming platforms, audience analytics, and digital asset management to deliver engaging experiences.',
      problem: 'Content scattered across systems. Limited audience insights. Manual publishing workflows. Poor content discoverability.',
      solution: 'Unified content management system with streaming capabilities, audience analytics, automated publishing workflows, and AI-powered content recommendations.',
      impact: [
        '10M+ users reached',
        '99.9% streaming uptime',
        '50% faster content publishing',
        '85% audience engagement'
      ]
    }
  ];

  return (
    <div className="home">
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Enterprise Software Solutions for Modern Business
              </h1>
              <p className="hero-description">
                We deliver custom software development, cloud infrastructure, and digital transformation 
                services that drive measurable business outcomes. Trusted by organizations across retail, 
                healthcare, education, and manufacturing sectors.
              </p>
              <div className="hero-actions">
                <Link to="/matrix">
                  <Button 
                    variant="contained" 
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      backgroundColor: '#2271b1',
                      color: '#ffffff',
                      fontWeight: '600',
                      padding: '14px 32px',
                      fontSize: '15px',
                      textTransform: 'none',
                      borderRadius: '3px',
                      boxShadow: '0 1px 0 #135e96',
                      
                      '&:hover': {
                        backgroundColor: '#135e96',
                        
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                      Start Your Project
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#2271b1',
                      color: '#02070cff',
                      fontWeight: '600',
                      padding: '14px 32px',
                      borderRadius: '3px',
                      clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)',
                      textTransform: 'none',
                      transform: 'skewX(-5deg)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#135e96',
                        backgroundColor: '#f6f7f7',
                        color: '#104269ff'
                      }
                    }}
                  >
                    View Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="industries-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Industry Expertise</h2>
            <p className="section-subtitle">
              Delivering tailored solutions across key business sectors
            </p>
          </div>

          <div className="industries-grid">
            {organizations.map((org, index) => (
              <div key={index} className="industry-card">
                <div className="industry-image-container">
                  <img src={org.image} alt={org.title} className="industry-image" />
                  <div className="industry-overlay">
                    <span className="industry-badge">{org.type}</span>
                  </div>
                </div>
                
                <div className="industry-body">
                  <h3 className="industry-title">{org.title}</h3>
                  <p className="industry-description">{org.description}</p>
                  
                  <div className="industry-metrics">
                    {org.impact.slice(0, 2).map((item, idx) => {
                      const match = item.match(/^(\d+%|\d+x)/);
                      const value = match ? match[0] : item.split(' ')[0];
                      const label = item.replace(/^(\d+%|\d+x)\s*/, '');
                      
                      return (
                        <div key={idx} className="metric-item">
                          <span className="metric-value">{value}</span>
                          <span className="metric-label">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="capabilities-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Core Capabilities</h2>
            <p className="section-subtitle">
              Comprehensive technology services to accelerate your digital initiatives
            </p>
          </div>

          <div className="capabilities-grid">
            <div className="capability-card">
              <h3>Custom Software Development</h3>
              <p>Enterprise applications built with modern frameworks and best practices for scalability and performance.</p>
            </div>
            <div className="capability-card">
              <h3>System Integration</h3>
              <p>Seamless integration of disparate systems to create unified, efficient business processes.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="workflow-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Development Process</h2>
            <p className="section-subtitle">
              A proven methodology that delivers results
            </p>
          </div>

          <div className="workflow-diagram">
            <div className="workflow-step">
              <div className="step-number">01</div>
              <h3>Discovery</h3>
              <p>Requirements gathering and technical assessment</p>
            </div>
            <div className="workflow-arrow">→</div>
            <div className="workflow-step">
              <div className="step-number">02</div>
              <h3>Design</h3>
              <p>Architecture planning and  design</p>
            </div>
            <div className="workflow-arrow">→</div>
            <div className="workflow-step">
              <div className="step-number">03</div>
              <h3>Development</h3>
              <p>Agile sprints with continuous integration</p>
            </div>
            <div className="workflow-arrow">→</div>
            <div className="workflow-step">
              <div className="step-number">04</div>
              <h3>Deployment</h3>
              <p>Testing, launch, and ongoing support</p>
            </div>
          </div>
        </div>
      </section>

     
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Start Your Digital Transformation</h2>
            <p>Partner with us to build scalable, secure, and efficient technology solutions.</p>
            <Link to="/contact">
              <Button 
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: '#2271b1',
                  color: '#ffffff',
                  fontWeight: '600',
                  padding: '14px 32px',
                  fontSize: '15px',
                  textTransform: 'none',
                  borderRadius: '3px',
                  boxShadow: '0 1px 0 #135e96',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#135e96',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
